import { useEffect, useCallback } from 'react';

/**
 * useModal - Hook para manejar lógica común de modales
 *
 * Centraliza:
 * - Bloqueo de scroll del body cuando el modal está abierto
 * - Cierre con tecla Escape
 * - Handler para click en backdrop
 *
 * @param {boolean} isOpen - Estado de visibilidad del modal
 * @param {function} onClose - Función para cerrar el modal
 * @param {Object} options - Opciones adicionales
 * @param {boolean} options.loading - Si está en estado de carga (bloquea cierre)
 * @returns {{ handleBackdropClick: function }} - Handler para click en backdrop
 */
function useModal(isOpen, onClose, options = {}) {
  const { loading = false } = options;

  // Prevenir scroll del body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // Cerrar con Escape
  useEffect(() => {
    /**
     * Cierra modal al presionar Escape
     * @param {KeyboardEvent} e - Evento de teclado
     */
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !loading) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  /**
   * Cierra modal al hacer click en el backdrop
   * @param {MouseEvent} e - Evento de click
   */
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  }, [loading, onClose]);

  return { handleBackdropClick };
}

export default useModal;
